import React, { useState, useEffect } from "react";
import i18n from "../i18n.js";
import { useNavigate, useLocation } from "react-router-dom";
import Button from "@mui/material/Button";
import { useForm, Controller } from "react-hook-form";
import Box from "@mui/material/Box";
import LinearProgress from "@mui/material/LinearProgress";
import TextField from "@mui/material/TextField";
import Stack from "@mui/material/Stack";
import Container from "@mui/material/Container";
import axios from "axios";
import Divider from "@mui/material/Divider";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";

const FormSolo = (props) => {
    const { itemOptions } = props;
    const { api } = itemOptions;
    const { t } = i18n;
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [countries, setCountries] = useState([]);
    const [code, setCode] = useState("");
    const [hobby, setHobby] = useState("");
    const [person, setPerson] = useState({});
    const { state } = useLocation();

    useEffect(() => {
        setCode(state.code);
        setHobby(state.hobby);
    }, [state]);

    const {
        handleSubmit,
        control,
        reset,
        formState: { errors, isValid },
    } = useForm({
        defaultValues: {
            firstName: "",
            lastName: "",
            year: "",
            email: "",
            city: "",
            club: "",
            country: "",
        },
    });

    useEffect(() => {
        const fetchData = async () => {
            setLoading(true);
            try {
                const countryResponse = await axios.get(`${api.getCountryList}`);
                if (countryResponse.status === 200) {
                    let tmp = [];
                    countryResponse.data.forEach((element) => {
                        tmp.push({ label: element.name, id: element.id });
                    });
                    if (hobby === true) {
                        tmp.push({ label: "Poland", id: 64 });
                        tmp.sort((a, b) => a.label.localeCompare(b.label));
                    }
                    setCountries(tmp);

                    reset({
                        firstName: "",
                        lastName: "",
                        year: "",
                        email: "",
                        city: "",
                        club: "",
                        country: hobby === true ? "Poland" : "Albania",
                    });
                }

                if (hobby === true) {
                    const personResponse = await axios.get(`${api.apiGetPerson}`);
                    const p = personResponse.data;
                    setPerson(p);
                    if (p.clubs.length > 0) {
                        const club = p.clubs[0];
                        reset({
                            firstName: "",
                            lastName: "",
                            year: "",
                            email: club.email,
                            city: club.city,
                            club: club.shortName,
                            country: "Poland",
                        });
                    }
                }
            } catch (error) {
                console.error(error);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [hobby, api, reset]);

    const onSubmit = (data) => {
        if (isValid || Object.entries(errors).length === 0) {
            navigate("/categories", {
                state: {
                    firstName: data.firstName,
                    lastName: data.lastName,
                    year: data.year,
                    firstName2: null,
                    lastName2: null,
                    idCountry: data.country,
                    city: data.city,
                    club: data.club,
                    year2: null,
                    couple: null,
                    team: null,
                    email: data.email,
                    fromWdsf: false,
                    fromForm: true,
                    isSolo: true,
                    isCouple: false,
                    isTeam: false,
                    isHobby: hobby,
                    code: code,
                },
            });
        } else {
            console.log(errors);
        }
    };

    const onError = (errors) => {
        console.log(errors);
    };

    return (
        <React.Fragment>
            <hr />
            <h2 className="display-6 mt-2">{t("reg.wdsf.solo")}</h2>
            <p>{t("reg.info.form.couple")}</p>
            {!loading && (
                <Container maxWidth="xs" className="mb-2">
                    <Box>
                        <form onSubmit={handleSubmit(onSubmit, onError)}>
                            <Stack direction="column" spacing={{ xs: 1, sm: 1, md: 2 }}>
                                <Controller
                                    name="firstName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={`${t("first_name")}`}
                                            type="text"
                                            required
                                            error={errors.firstName !== undefined}
                                            helperText={errors.firstName !== undefined ? t("reg.wdsf.helper.not_empty") : ""}
                                        />
                                    )}
                                />
                                <Controller
                                    name="lastName"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={`${t("last_name")}`}
                                            type="text"
                                            required
                                            error={errors.lastName !== undefined}
                                            helperText={errors.lastName !== undefined ? t("reg.wdsf.helper.not_empty") : ""}
                                        />
                                    )}
                                />
                                <Controller
                                    name="year"
                                    control={control}
                                    rules={{ required: true, min: 1900, max: 2099 }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={`${t("reg.birth_year")}`}
                                            type="number"
                                            min="1900"
                                            max="2099"
                                            step="1"
                                            required
                                            error={errors.year !== undefined}
                                            helperText={errors.year !== undefined ? t("reg.wdsf.helper.year") : ""}
                                        />
                                    )}
                                />
                                <Divider />
                                <Controller
                                    name="city"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={t("city")}
                                            type="text"
                                            required
                                            error={errors.city !== undefined}
                                            helperText={errors.city !== undefined ? t("reg.wdsf.helper.not_empty") : ""}
                                        />
                                    )}
                                />
                                <Controller
                                    name="club"
                                    control={control}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={t("club")}
                                            type="text"
                                            error={errors.club !== undefined}
                                            helperText={errors.club !== undefined ? t("reg.wdsf.helper.not_empty") : ""}
                                        />
                                    )}
                                />
                                {hobby !== true && (
                                    <Controller
                                        name="country"
                                        control={control}
                                        render={({ field }) => (
                                            <FormControl fullWidth sx={{ textAlign: "left" }}>
                                                <InputLabel id="country-label">{t("country")}</InputLabel>
                                                <Select {...field} labelId="country-label" label={t("country")}>
                                                    {countries.map((c) => (
                                                        <MenuItem key={c.id} value={c.label}>
                                                            {c.label}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                        )}
                                    />
                                )}
                                <Controller
                                    name="email"
                                    control={control}
                                    rules={{
                                        pattern:
                                            /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/,
                                    }}
                                    render={({ field }) => (
                                        <TextField
                                            {...field}
                                            label={t("email")}
                                            type="email"
                                            required
                                            error={errors.email !== undefined}
                                            helperText={errors.email !== undefined ? t("reg.wdsf.helper.email") : ""}
                                        />
                                    )}
                                />

                                {!loading && (
                                    <Button type="submit" variant="outlined">
                                        {t("confirm_proceed_to_categories")}
                                    </Button>
                                )}
                                {loading && <LinearProgress />}
                            </Stack>
                        </form>
                    </Box>
                </Container>
            )}
        </React.Fragment>
    );
};

export default FormSolo;
